import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

// Free gift campaigns disabled (2026-10-01) — thresholds set unreachable so any
// existing auto-gift lines in open carts are cleaned up and no new ones are added.
const GIFT_RULES = [
  { threshold: Infinity, variantId: 67389948494114 }, // Ivy Leaf Adjustable Ring - was $75+
  { threshold: Infinity, variantId: 67389960585506 }, // Twisted Rope Hoop Earrings - was $100+
];
const GIFT_VARIANT_IDS = GIFT_RULES.map((rule) => rule.variantId);
const SOURCE = 'cart-free-gifts';
const GIFT_PROPERTY_KEY = '_shera_free_gift';
const GIFT_PROPERTY_VALUE = 'auto';

class CartFreeGifts {
  #syncing = false;
  #controller = new AbortController();

  constructor() {
    const { signal } = this.#controller;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#sync(), { signal, once: true });
    } else {
      this.#sync();
    }
  }

  destroy() { this.#controller.abort(); }

  #onCartUpdate = (event) => {
    if (event?.detail?.source === SOURCE) return;
    event.promise?.then(() => this.#sync()).catch(() => {});
  };

  async #getCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  #isAutoGiftLine(item) {
    return GIFT_VARIANT_IDS.includes(item.variant_id) && item.properties && item.properties[GIFT_PROPERTY_KEY] === GIFT_PROPERTY_VALUE;
  }

  #nonGiftSubtotalCents(cart) {
    return cart.items.reduce((sum, item) => {
      if (this.#isAutoGiftLine(item)) return sum;
      return sum + item.line_price;
    }, 0);
  }

  async #sync() {
    if (this.#syncing) return;
    this.#syncing = true;
    try {
      const cart = await this.#getCart();
      const subtotal = this.#nonGiftSubtotalCents(cart);

      const additions = [];
      const removals = [];

      for (const rule of GIFT_RULES) {
        const giftLine = cart.items.find((item) => this.#isAutoGiftLine(item) && item.variant_id === rule.variantId);
        const shouldHaveGift = subtotal >= rule.threshold;

        if (shouldHaveGift && !giftLine) {
          additions.push({ id: rule.variantId, quantity: 1, properties: { [GIFT_PROPERTY_KEY]: GIFT_PROPERTY_VALUE } });
        } else if (!shouldHaveGift && giftLine) {
          removals.push(giftLine.key);
        }
      }

      if (additions.length === 0 && removals.length === 0) return;
      await this.#applyUpdates({ additions, removals });
    } catch (error) {
      console.warn('[cart-free-gifts] sync failed', error);
    } finally {
      this.#syncing = false;
    }
  }

  async #applyUpdates({ additions, removals }) {
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: 'update', context: 'cart',
      lines: [
        ...additions.map((line) => ({ merchandiseId: line.id, quantity: line.quantity })),
        ...removals.map((key) => ({ merchandiseId: key, quantity: 0 })),
      ],
      promise: deferredEventPromise.promise,
    }));
    try {
      for (const key of removals) {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
        const result = await response.json();
        if (result.status) throw new Error(result.message || 'Failed to remove free gift');
      }

      let lastCart = null;
      if (additions.length > 0) {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: additions }),
        });
        const result = await response.json();
        if (result.status) throw new Error(result.message || result.description || 'Failed to add free gift');
      }

      const finalCartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      lastCart = await finalCartResponse.json();

      const cartItemsComponent = document.querySelector('cart-items-component');
      if (cartItemsComponent) {
        await customElements.whenDefined('cart-items-component');
        await cartItemsComponent.fetchCartData();
      }

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(lastCart),
        detail: { items: lastCart.items, source: SOURCE, sourceId: SOURCE, didError: false },
      });
    } catch (error) {
      deferredEventPromise.reject(error);
      throw error;
    }
  }
}

if (!window.__cartFreeGiftsInitialized) {
  window.__cartFreeGiftsInitialized = true;
  new CartFreeGifts();
}
